import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    // Always send/receive cookies, including on Safari where ITP can otherwise
    // treat the same-origin cookie as third-party in some redirect chains.
    withCredentials: true,
});

api.interceptors.request.use((config) => {
    // Bearer fallback when the HttpOnly cookie is dropped by the browser
    // (notably iOS Safari ITP after a magic-link 302 redirect chain).
    const token = localStorage.getItem("ctx_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Forward preview key as a request header so the backend honours member
    // previews even when the URL has been stripped (deep-linked SPA nav).
    try {
        let key = null;
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            key = params.get("preview") || params.get("member");
            if (!key) key = window.sessionStorage.getItem("ctx_preview_key");
        }
        if (key) config.headers["X-Preview-Key"] = key;
    } catch {
        /* ignore */
    }
    return config;
});

// Routes where a 401 is a legitimate auth probe (not a stale-session failure)
// and we MUST NOT clear the token / fire the unauthorized event. Without this
// guard, the AuthProvider's initial /auth/me probe for anonymous users would
// trigger the global session-expired handler.
const AUTH_PROBE_ROUTES = ["/auth/me", "/auth/claim-status"];

function isAuthProbe(url) {
    if (!url) return false;
    return AUTH_PROBE_ROUTES.some((p) => url.includes(p));
}

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        if (status === 401 && !isAuthProbe(url)) {
            // Stale token / cookie eviction — clear the Bearer fallback so we
            // don't keep retrying with a dead JWT. AuthContext listens for
            // the event and re-runs its /auth/me probe to reset UI state.
            try { localStorage.removeItem("ctx_token"); } catch { /* ignore */ }
            try {
                window.dispatchEvent(new CustomEvent("ctx:unauthorized"));
            } catch { /* ignore */ }
        }
        return Promise.reject(error);
    }
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default api;
